const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const loadGenerationByImage = new WeakMap();
const pendingSrcClearByImage = new WeakMap();

const nextLoadGeneration = (imageEl) => {
    const next = (loadGenerationByImage.get(imageEl) || 0) + 1;
    loadGenerationByImage.set(imageEl, next);
    return next;
};

export const createImageCrop = ({
    viewportEl,
    imageEl,
    zoomEl,
    outputWidth = 500,
    outputHeight = 500,
    mimeType = 'image/jpeg',
    quality = 0.9
}) => {
    const state = {
        x: 0,
        y: 0,
        scale: 1,
        coverScale: 1,
        zoom: MIN_ZOOM,
        naturalW: 0,
        naturalH: 0,
        viewportW: 0,
        viewportH: 0,
        dragging: false,
        pointerId: null,
        lastX: 0,
        lastY: 0
    };

    const listeners = [];
    const attach = (el, type, fn, opts) => {
        if (!el) return;
        el.addEventListener(type, fn, opts);
        listeners.push(() => el.removeEventListener(type, fn, opts));
    };

    const clampPan = () => {
        const { naturalW, naturalH, scale, viewportW, viewportH } = state;
        const scaledW = naturalW * scale;
        const scaledH = naturalH * scale;
        const minX = Math.min(0, viewportW - scaledW);
        const minY = Math.min(0, viewportH - scaledH);
        state.x = clamp(state.x, minX, 0);
        state.y = clamp(state.y, minY, 0);
    };

    const applyTransform = () => {
        imageEl.style.maxWidth = 'none';
        imageEl.style.maxHeight = 'none';
        imageEl.style.width = `${state.naturalW}px`;
        imageEl.style.height = `${state.naturalH}px`;
        imageEl.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
    };

    const setZoom = (zoom, { aroundViewportX, aroundViewportY } = {}) => {
        const nextZoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
        const nextScale = state.coverScale * nextZoom;
        const focusX = aroundViewportX ?? state.viewportW / 2;
        const focusY = aroundViewportY ?? state.viewportH / 2;
        const imageX = (focusX - state.x) / state.scale;
        const imageY = (focusY - state.y) / state.scale;
        state.zoom = nextZoom;
        state.scale = nextScale;
        state.x = focusX - imageX * nextScale;
        state.y = focusY - imageY * nextScale;
        clampPan();
        applyTransform();
        if (zoomEl && Number(zoomEl.value) !== nextZoom) zoomEl.value = String(nextZoom);
    };

    const measureAndFit = () => {
        state.viewportW = viewportEl.clientWidth;
        state.viewportH = viewportEl.clientHeight;
        if (!state.viewportW || !state.viewportH || !state.naturalW || !state.naturalH) return;
        state.coverScale = Math.max(state.viewportW / state.naturalW, state.viewportH / state.naturalH);
        state.scale = state.coverScale * state.zoom;
        state.x = (state.viewportW - state.naturalW * state.scale) / 2;
        state.y = (state.viewportH - state.naturalH * state.scale) / 2;
        clampPan();
        applyTransform();
    };

    const handlePointerDown = (event) => {
        if (event.button !== 0 && event.pointerType === 'mouse') return;
        state.dragging = true;
        state.pointerId = event.pointerId;
        state.lastX = event.clientX;
        state.lastY = event.clientY;
        viewportEl.classList.add('is-dragging');
        viewportEl.setPointerCapture(event.pointerId);
        event.preventDefault();
    };

    const handlePointerMove = (event) => {
        if (!state.dragging || event.pointerId !== state.pointerId) return;
        state.x += event.clientX - state.lastX;
        state.y += event.clientY - state.lastY;
        state.lastX = event.clientX;
        state.lastY = event.clientY;
        clampPan();
        applyTransform();
    };

    const handlePointerUp = (event) => {
        if (event.pointerId !== state.pointerId) return;
        state.dragging = false;
        state.pointerId = null;
        viewportEl.classList.remove('is-dragging');
        if (viewportEl.hasPointerCapture(event.pointerId)) {
            viewportEl.releasePointerCapture(event.pointerId);
        }
    };

    const handleWheel = (event) => {
        event.preventDefault();
        const rect = viewportEl.getBoundingClientRect();
        const factor = event.deltaY > 0 ? 0.92 : 1.08;
        setZoom(state.zoom * factor, {
            aroundViewportX: event.clientX - rect.left,
            aroundViewportY: event.clientY - rect.top
        });
    };

    const handleZoomInput = () => {
        setZoom(Number(zoomEl.value) || MIN_ZOOM);
    };

    attach(viewportEl, 'pointerdown', handlePointerDown);
    attach(viewportEl, 'pointermove', handlePointerMove);
    attach(viewportEl, 'pointerup', handlePointerUp);
    attach(viewportEl, 'pointercancel', handlePointerUp);
    attach(viewportEl, 'wheel', handleWheel, { passive: false });
    if (zoomEl) {
        zoomEl.min = String(MIN_ZOOM);
        zoomEl.max = String(MAX_ZOOM);
        zoomEl.step = '0.01';
        zoomEl.value = String(MIN_ZOOM);
        attach(zoomEl, 'input', handleZoomInput);
    }

    let detachImageLoad = null;
    const clearImageLoadListeners = () => {
        detachImageLoad?.();
        detachImageLoad = null;
    };

    const handleViewportResize = () => {
        const nextW = viewportEl.clientWidth;
        const nextH = viewportEl.clientHeight;
        if (nextW === state.viewportW && nextH === state.viewportH) return;
        measureAndFit();
    };
    const resizeObserver = typeof ResizeObserver === 'function'
        ? new ResizeObserver(handleViewportResize)
        : null;
    resizeObserver?.observe(viewportEl);

    const load = (src) => new Promise((resolve, reject) => {
        const generation = nextLoadGeneration(imageEl);
        const begin = () => {
            if (loadGenerationByImage.get(imageEl) !== generation) return;
            clearImageLoadListeners();
            let settled = false;
            const finish = (fn) => {
                if (settled) return;
                settled = true;
                clearImageLoadListeners();
                fn();
            };
            const handleLoad = () => {
                if (loadGenerationByImage.get(imageEl) !== generation) return;
                state.naturalW = imageEl.naturalWidth;
                state.naturalH = imageEl.naturalHeight;
                if (!state.naturalW || !state.naturalH) {
                    finish(() => reject(new Error('Invalid image')));
                    return;
                }
                state.zoom = MIN_ZOOM;
                measureAndFit();
                finish(resolve);
            };
            const handleError = () => {
                if (loadGenerationByImage.get(imageEl) !== generation) return;
                const current = imageEl.getAttribute('src');
                if (!current || current !== src) return;
                finish(() => reject(new Error('Failed to load image')));
            };
            imageEl.addEventListener('load', handleLoad);
            imageEl.addEventListener('error', handleError);
            detachImageLoad = () => {
                imageEl.removeEventListener('load', handleLoad);
                imageEl.removeEventListener('error', handleError);
            };
            if (imageEl.parentElement !== viewportEl) viewportEl.appendChild(imageEl);
            imageEl.src = src;
            if (imageEl.complete && imageEl.naturalWidth) handleLoad();
        };
        if (pendingSrcClearByImage.get(imageEl)) {
            pendingSrcClearByImage.delete(imageEl);
            setTimeout(begin, 0);
            return;
        }
        begin();
    });

    const sourceRect = () => {
        if (!state.naturalW || !state.naturalH || !state.viewportW || !state.viewportH || !state.scale) {
            throw new Error('No crop loaded');
        }
        const srcW = Math.min(state.naturalW, state.viewportW / state.scale);
        const srcH = Math.min(state.naturalH, state.viewportH / state.scale);
        const srcX = clamp(-state.x / state.scale, 0, Math.max(0, state.naturalW - srcW));
        const srcY = clamp(-state.y / state.scale, 0, Math.max(0, state.naturalH - srcH));
        return { x: srcX, y: srcY, width: srcW, height: srcH };
    };

    const drawCrop = (outW, outH) => {
        const { x: srcX, y: srcY, width: srcW, height: srcH } = sourceRect();
        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas is not available');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(imageEl, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
        return canvas;
    };

    const exportCrop = (overrides = {}) => {
        const outW = overrides.outputWidth ?? outputWidth;
        const outH = overrides.outputHeight ?? outputHeight;
        const type = overrides.mimeType ?? mimeType;
        const q = overrides.quality ?? quality;
        return drawCrop(outW, outH).toDataURL(type, q);
    };

    const exportCropBlob = (overrides = {}) => new Promise((resolve, reject) => {
        const outW = overrides.outputWidth ?? outputWidth;
        const outH = overrides.outputHeight ?? outputHeight;
        const type = overrides.mimeType ?? mimeType;
        const q = overrides.quality ?? quality;
        drawCrop(outW, outH).toBlob((blob) => {
            if (!blob) {
                reject(new Error('Failed to export crop'));
                return;
            }
            resolve(blob);
        }, type, q);
    });

    const destroy = () => {
        clearImageLoadListeners();
        nextLoadGeneration(imageEl);
        pendingSrcClearByImage.set(imageEl, true);
        resizeObserver?.disconnect();
        listeners.forEach((detach) => detach());
        listeners.length = 0;
        viewportEl.classList.remove('is-dragging');
        imageEl.removeAttribute('src');
        imageEl.removeAttribute('style');
    };

    return { load, exportCrop, exportCropBlob, exportSourceRect: sourceRect, destroy, measureAndFit };
};
